import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize, TextType} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {AvatarPlaceholderIcon} from '@Components/avatar-placeholder-icon/avatar-placeholder-icon';
import {AvatarBorderThickness, AvatarSize} from '@Components/avatar-placeholder-icon/avatar-placeholder-icon.types';
import {getAvatarPlaceholderPropsForName} from '@Components/avatar-placeholder-icon/avatar-placeholder-icon.util';
import {ClickableDiv} from '@Components/clickable-div';
import {formatTime, getDateAndMonthStringWithOrdinal, getTimeAndDateLabel, isToday} from '@Utils/date.util';
import type {VersionHistoryListItemProps} from './version-history-list-item.types';
import styles from './version-history-list-item.module.scss';

function VersionHistoryListItem({version, index, isSelected, isLoading, onVersionSelect}: VersionHistoryListItemProps): ReactElement {
  const handleClick = (): void => {
    if (!isLoading) {
      void onVersionSelect(index);
    }
  };

  const renderIconContent = (): ReactElement => {
    if (isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <CircularProgressLoader color={LOADER_COLOR.PRIMARY_DARK} size={LOADER_SIZE.XXSMALL} />
        </div>
      );
    }

    return (
      <Icon
        icon={isSelected ? 'icon-current-version' : 'icon-old-version'}
        className="content-sub-text"
        size={IconSize.SIZE_ICON_12}
        type={IconType.FLAT}
        flatIconType={{
          color: isSelected ? FlatIconColor.PRIMARY : FlatIconColor.NONE,
          type: FlatIconColorType.DEFAULT,
        }}
        shape={IconShape.CIRCLE}
      />
    );
  };

  const renderIcon = (): ReactElement => {
    return (
      <div className="spacing-p-t-2">
        <div className={`flexbox flex-center _position-relative ${styles.versionIconWrapper}`}>
          {renderIconContent()}
        </div>
      </div>
    );
  };

  const getFormattedTimestamp = (): string => {
    return getTimeAndDateLabel(version.timestamp);
  };

  const getVersionTitle = (): string => {
    if (isLoading) {
      return window.i18next.t('pmwjs_fetching_version');
    }

    if (index === 0) {
      const versionDate = new Date(version.timestamp * 1000);
      return isToday(versionDate)
          ? `${window.i18next.t('pmwjs_current_version')}, ${formatTime(version.timestamp)}`
          : `${window.i18next.t('pmwjs_current_version')}, ${getDateAndMonthStringWithOrdinal(versionDate)}`;
    }

    return getFormattedTimestamp();
  };

  const getTextColorClass = (): string => {
    return isSelected ? 'content-primary' : 'content-body';
  };

  const renderTitle = (): ReactElement => {
    const versionTitle = getVersionTitle();
    return (
      <Text
        val={versionTitle}
        type={TextType.BODY}
        className={getTextColorClass()}
        size={TextSize.MD}
        bold={isSelected}
        title={versionTitle}
      />
    );
  };

  const renderAuthorInfo = (): ReactElement => {
    if (isLoading) {
      return (
        <div className={`flexbox flex-row-align-center ${styles.authorInfo}`}>
          <Text val={getFormattedTimestamp()} className="content-primary" size={TextSize.SM} />
        </div>
      );
    }

    const avatarProps = getAvatarPlaceholderPropsForName(version.authorName);

    return (
      <div className={`flexbox flex-row-align-center gap-1 ${styles.authorInfo}`}>
        <AvatarPlaceholderIcon
          placeholderLetter={avatarProps.placeholderLetter}
          colorClass={avatarProps.colorClass}
          size={AvatarSize.XSMALL}
          borderThickness={AvatarBorderThickness.THIN}
        />
        <Text val={version.authorName} type={TextType.BODY} size={TextSize.SM} className="content-sub-text" />
      </div>
    );
  };

  const renderContent = (): ReactElement => {
    return (
      <div className={`flexbox flex-column-justify-start flex-items-start flex-1 ${styles.versionContent}`}>
        {renderTitle()}
        {renderAuthorInfo()}
      </div>
    );
  };

  return (
    <li className={`${styles.versionItem} ${isSelected ? styles.selected : ''}`}>
      <ClickableDiv className={`_cursor-pointer flexbox flex-items-start ${styles.versionItemContent}`} onClick={handleClick}>
        {renderIcon()}
        {renderContent()}
      </ClickableDiv>
    </li>
  );
}

export default React.memo(VersionHistoryListItem);
